import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { appConfig } from '../config/app'
import type { ThemeMode } from '../types'

interface ThemeContextValue {
  mode: ThemeMode
  resolvedTheme: 'light' | 'dark'
  primary: string
  setMode: (mode: ThemeMode) => void
  setPrimary: (color: string) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)
const getSystemTheme = () => window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(appConfig.storageKeys.theme)
    return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system'
  })
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(getSystemTheme)
  const [primary, setPrimaryState] = useState(() => localStorage.getItem(appConfig.storageKeys.primary) ?? '#2563eb')
  const resolvedTheme = mode === 'system' ? systemTheme : mode

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const sync = () => setSystemTheme(media.matches ? 'dark' : 'light')
    media.addEventListener('change', sync)
    return () => media.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme
    document.documentElement.setAttribute('data-bs-theme', resolvedTheme)
  }, [resolvedTheme])

  useEffect(() => {
    document.documentElement.style.setProperty('--admin-primary', primary)
  }, [primary])

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    localStorage.setItem(appConfig.storageKeys.theme, next)
  }, [])

  const setPrimary = useCallback((color: string) => {
    setPrimaryState(color)
    localStorage.setItem(appConfig.storageKeys.primary, color)
  }, [])

  const value = useMemo(
    () => ({ mode, resolvedTheme, primary, setMode, setPrimary }),
    [mode, primary, resolvedTheme, setMode, setPrimary],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// Context hooks intentionally live beside the provider during the foundation phase.
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used within ThemeProvider')
  return context
}
